import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  ApplyInterviewPlanInput,
  InterviewKitRequest,
  InterviewKitView,
  InterviewPlanOptions,
  JobDetail,
  PlanSeatLeftOut,
  PlanTemplateRequest,
  PlanTemplateView,
  RubricRequest,
  RubricVersionView,
  RubricView,
} from '@nest-recruit/shared';
import { ApiError, apiGet, apiPatch, apiPost, apiPut } from '../../../lib/api-client';
import { JOBS_KEY, jobKey, stageBarKey } from './jobs-api';

// ADMIN-02 rubrics, ADMIN-04 interview kits, ADMIN-03 interview plans (S12; admin redesign batch 2), and the one place a plan
// reaches a job: "Start from a plan". Moved out of jobs-api.ts so the pages that own them change apart from the job's own hooks.
//
// Every list keeps what is on screen while it refetches (CLAUDE.md §5a rule 2). `isEnabled` lets a page ask only once it knows
// the viewer may open it (review W15: no refused request in the console).

/** A job's round-setup choices (`jobs-api` `useInterviewOptions`): the kits and plans it offers. */
function isInterviewOptions(query: { readonly queryKey: readonly unknown[] }): boolean {
  return query.queryKey[0] === JOBS_KEY[0] && query.queryKey.includes('interview-options');
}

export const RUBRICS_KEY = ['admin', 'rubrics'] as const;
export const KITS_KEY = ['admin', 'interview-kits'] as const;
export const PLANS_KEY = ['admin', 'interview-plans'] as const;

// --- ADMIN-02 ----------------------------------------------------------------------------------------------------------

export function useRubrics(isEnabled = true): UseQueryResult<readonly RubricView[], ApiError> {
  return useQuery<readonly RubricView[], ApiError>({
    queryKey: RUBRICS_KEY,
    queryFn: () => apiGet<readonly RubricView[]>('/admin/rubrics'),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

/** One version by its id — the current one or an older one (opened from the history line, read-only). */
export function useRubric(id: string | null, isEnabled = true): UseQueryResult<RubricView, ApiError> {
  return useQuery<RubricView, ApiError>({
    queryKey: [...RUBRICS_KEY, 'detail', id],
    queryFn: () => apiGet<RubricView>(`/admin/rubrics/${id ?? ''}`),
    enabled: isEnabled && id !== null,
  });
}

export function useRubricVersions(id: string | null, isEnabled = true): UseQueryResult<readonly RubricVersionView[], ApiError> {
  return useQuery<readonly RubricVersionView[], ApiError>({
    queryKey: [...RUBRICS_KEY, 'versions', id],
    queryFn: () => apiGet<readonly RubricVersionView[]>(`/admin/rubrics/${id ?? ''}/versions`),
    placeholderData: (previous) => previous,
    enabled: isEnabled && id !== null,
  });
}

/** Saving an existing rubric writes its next version; the answer is that version. Kits read their rubric's lines, so they refresh too. */
export function useSaveRubric() {
  const queryClient = useQueryClient();
  return useMutation<RubricView, ApiError, { id: string | null; version: number; body: RubricRequest }>({
    mutationFn: ({ id, version, body }) =>
      id ? apiPut<RubricView>(`/admin/rubrics/${id}`, { ...body, version }) : apiPost<RubricView>('/admin/rubrics', body),
    onSuccess: async (saved) => {
      queryClient.setQueryData([...RUBRICS_KEY, 'detail', saved.id], saved);
      await Promise.all([queryClient.invalidateQueries({ queryKey: RUBRICS_KEY }), queryClient.invalidateQueries({ queryKey: KITS_KEY })]);
    },
  });
}

// --- ADMIN-04 ----------------------------------------------------------------------------------------------------------

export function useInterviewKits(isEnabled = true): UseQueryResult<readonly InterviewKitView[], ApiError> {
  return useQuery<readonly InterviewKitView[], ApiError>({
    queryKey: KITS_KEY,
    queryFn: () => apiGet<readonly InterviewKitView[]>('/admin/interview-kits'),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

/** One version by its id, current or older. */
export function useInterviewKit(id: string | null, isEnabled = true): UseQueryResult<InterviewKitView, ApiError> {
  return useQuery<InterviewKitView, ApiError>({
    queryKey: [...KITS_KEY, 'detail', id],
    queryFn: () => apiGet<InterviewKitView>(`/admin/interview-kits/${id ?? ''}`),
    enabled: isEnabled && id !== null,
  });
}

export function useSaveInterviewKit() {
  const queryClient = useQueryClient();
  return useMutation<InterviewKitView, ApiError, { id: string | null; version: number; body: InterviewKitRequest }>({
    mutationFn: ({ id, version, body }) =>
      id ? apiPut<InterviewKitView>(`/admin/interview-kits/${id}`, { ...body, version }) : apiPost<InterviewKitView>('/admin/interview-kits', body),
    onSuccess: async (saved) => {
      queryClient.setQueryData([...KITS_KEY, 'detail', saved.id], saved);
      // A job's round picks from the current versions.
      await Promise.all([queryClient.invalidateQueries({ queryKey: KITS_KEY }), queryClient.invalidateQueries({ predicate: isInterviewOptions })]);
    },
  });
}

// --- ADMIN-03 ----------------------------------------------------------------------------------------------------------

export function usePlanTemplates(isEnabled = true): UseQueryResult<readonly PlanTemplateView[], ApiError> {
  return useQuery<readonly PlanTemplateView[], ApiError>({
    queryKey: PLANS_KEY,
    queryFn: () => apiGet<readonly PlanTemplateView[]>('/admin/interview-plans'),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

/** The people a plan's panel chooses from. */
export function usePlanOptions(isEnabled = true): UseQueryResult<InterviewPlanOptions, ApiError> {
  return useQuery<InterviewPlanOptions, ApiError>({
    queryKey: [...PLANS_KEY, 'options'],
    queryFn: () => apiGet<InterviewPlanOptions>('/admin/interview-plans/options'),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

export function useSavePlanTemplate() {
  const queryClient = useQueryClient();
  return useMutation<readonly PlanTemplateView[], ApiError, { id: string | null; version: number; body: PlanTemplateRequest }>({
    mutationFn: ({ id, version, body }) =>
      id
        ? apiPatch<readonly PlanTemplateView[]>(`/admin/interview-plans/${id}`, { ...body, version })
        : apiPost<readonly PlanTemplateView[]>('/admin/interview-plans', body),
    onSuccess: async (plans) => {
      queryClient.setQueryData(PLANS_KEY, plans);
      // "Start from a plan" on a job lists the plans as they are now.
      await queryClient.invalidateQueries({ predicate: isInterviewOptions });
    },
  });
}

// --- "Start from a plan" (F5.29) ---------------------------------------------------------------------------------------

export interface AppliedPlan {
  readonly job: JobDetail;
  readonly leftOut: readonly PlanSeatLeftOut[];
}

/** Copies a plan's rounds into the job; seeds the job with the server's answer, as every step's save does. */
export function useApplyInterviewPlan() {
  const queryClient = useQueryClient();
  return useMutation<AppliedPlan, ApiError, { readonly jobId: string; readonly body: ApplyInterviewPlanInput }>({
    mutationFn: ({ jobId, body }) => apiPost<AppliedPlan>(`/jobs/${jobId}/interview-plan/from-plan`, body),
    onSuccess: async ({ job }) => {
      queryClient.setQueryData(jobKey(job.id), job);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...JOBS_KEY, 'list'] }),
        queryClient.invalidateQueries({ queryKey: stageBarKey(job.id) }),
        queryClient.invalidateQueries({ queryKey: [...jobKey(job.id), 'interview-options'] }),
      ]);
    },
  });
}
